'use client';

/**
 * ConfigSyncStatus
 *
 * Badge component showing the current config sync status.
 */

import { Badge } from '@cortex/ui';
import {
  AlertCircle,
  CheckCircle2,
  Clock,
  FileQuestion,
  Loader2,
  XCircle,
} from 'lucide-react';

interface ConfigSyncStatusProps {
  status: string;
  className?: string;
}

export function ConfigSyncStatus({ status, className }: ConfigSyncStatusProps) {
  const getStatusConfig = () => {
    switch (status) {
      case 'synced':
        return {
          label: 'Synced',
          variant: 'success' as const,
          icon: CheckCircle2,
        };
      case 'syncing':
        return {
          label: 'Syncing',
          variant: 'info' as const,
          icon: Loader2,
          iconClassName: 'animate-spin',
        };
      case 'pending':
        return {
          label: 'Pending',
          variant: 'warning' as const,
          icon: Clock,
        };
      case 'not_found':
        return {
          label: 'Not Found',
          variant: 'outline' as const,
          icon: FileQuestion,
        };
      case 'invalid':
        return {
          label: 'Invalid',
          variant: 'destructive' as const,
          icon: AlertCircle,
        };
      case 'error':
        return {
          label: 'Error',
          variant: 'destructive' as const,
          icon: XCircle,
        };
      default:
        return {
          label: 'Unknown',
          variant: 'outline' as const,
          icon: AlertCircle,
        };
    }
  };

  const config = getStatusConfig();
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className={className}>
      <Icon className={`mr-1 h-3 w-3 ${config.iconClassName || ''}`} />
      {config.label}
    </Badge>
  );
}
